import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';

// Register font (optional, using standard Helvetica for now)

const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        padding: 30,
        fontFamily: 'Helvetica',
        fontSize: 10
    },
    header: {
        marginBottom: 20,
        paddingBottom: 10,
        borderBottomWidth: 1,
        borderBottomColor: '#eee',
        flexDirection: 'row',
        justifyContent: 'space-between'
    },
    headerLeft: {
        flexDirection: 'column'
    },
    headerRight: {
        alignItems: 'flex-end'
    },
    logo: {
        width: 100,
        marginBottom: 10
    },
    title: {
        fontSize: 24,
        fontWeight: 'bold',
        textTransform: 'uppercase',
        color: '#333'
    },
    subtitle: {
        fontSize: 10,
        color: '#666',
        marginTop: 5
    },
    billTo: {
        marginTop: 20,
        marginBottom: 20,
        padding: 10,
        backgroundColor: '#f9f9f9',
        borderRadius: 4
    },
    billToTitle: {
        fontWeight: 'bold',
        marginBottom: 5,
        fontSize: 11,
        color: '#333'
    },
    table: {
        marginTop: 10,
        width: '100%',
    },
    tableHeader: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#000',
        paddingBottom: 5,
        marginBottom: 5,
        fontWeight: 'bold',
        backgroundColor: '#f0f0f0',
        padding: 8
    },
    tableRow: {
        flexDirection: 'row',
        paddingVertical: 5,
        borderBottomWidth: 1,
        borderBottomColor: '#eee',
        paddingHorizontal: 8
    },
    colDesc: { width: '50%' },
    colQty: { width: '10%', textAlign: 'center' },
    colPrice: { width: '20%', textAlign: 'right' },
    colTotal: { width: '20%', textAlign: 'right' },
    
    totalsSection: {
        marginTop: 20,
        alignItems: 'flex-end'
    },
    totalRow: {
        flexDirection: 'row',
        marginBottom: 3,
        width: 200,
        justifyContent: 'space-between'
    },
    totalLabel: {
        fontWeight: 'bold',
        color: '#666'
    },
    totalValue: {
        color: '#333'
    },
    grandTotal: {
        borderTopWidth: 1,
        borderTopColor: '#333',
        paddingTop: 5,
        marginTop: 5,
        fontWeight: 'bold',
        fontSize: 14
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 30,
        right: 30,
        textAlign: 'center',
        paddingTop: 10,
        borderTopWidth: 1,
        borderTopColor: '#eee',
        color: '#888',
        fontSize: 8
    }
});

const InvoicePDF = ({ invoice, items, payments }) => {
    if (!invoice) return <Document><Page size="A4"><Text>No Invoice Data</Text></Page></Document>;

    const formatCurrency = (amount) => {
        return Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString();
    };

    return (
        <Document title={`Invoice-${invoice.inv_ref}`}>
            <Page size="A4" style={styles.page}>
                {/* Header */}
                <View style={styles.header}>
                    <View style={styles.headerLeft}>
                        {/* Assuming logo exists in public folder as sitelogo.png */}
                        <Image src="/sitelogo.png" style={styles.logo} />
                        <Text style={{ fontSize: 12, fontWeight: 'bold' }}>GYM SYSTEM</Text>
                        <Text>123 Fitness Street</Text>
                        <Text>Colombo, Sri Lanka</Text>
                        <Text>Contact: +94 77 123 4567</Text>
                    </View>
                    <View style={styles.headerRight}>
                        <Text style={styles.title}>INVOICE</Text>
                        <Text style={styles.subtitle}>#{invoice.inv_ref}</Text>
                        <Text style={styles.subtitle}>Date: {new Date(invoice.process_dt).toLocaleString()}</Text>
                         {/* Status */}
                         <Text style={{ 
                            marginTop: 10, 
                            color: invoice.inv_status === 'Completed' ? 'green' : invoice.inv_status === 'Cancelled' ? 'red' : 'orange',
                            fontWeight: 'bold',
                            borderWidth: 1,
                            borderColor: invoice.inv_status === 'Completed' ? 'green' : invoice.inv_status === 'Cancelled' ? 'red' : 'orange',
                            padding: '2 8',
                            borderRadius: 4
                        }}>{invoice.inv_status.toUpperCase()}</Text>
                    </View>
                </View>

                {/* Bill To */}
                <View style={styles.billTo}>
                    <Text style={styles.billToTitle}>BILL TO:</Text>
                    <Text style={{ fontWeight: 'bold', fontSize: 12 }}>{invoice.member_name}</Text>
                    <Text>ID: {invoice.member_code}</Text>
                    <Text>{invoice.member_phone}</Text>
                    {invoice.member_email && <Text>{invoice.member_email}</Text>}
                </View>

                {/* Items Table */}
                <View style={styles.table}>
                    <View style={styles.tableHeader}>
                        <Text style={styles.colDesc}>Description</Text>
                        <Text style={styles.colQty}>Qty</Text>
                        <Text style={styles.colPrice}>Unit Price</Text>
                        <Text style={styles.colTotal}>Amount</Text>
                    </View>

                    {items.map((item, index) => (
                        <View key={index} style={styles.tableRow}>
                            <View style={styles.colDesc}>
                                <Text style={{ fontWeight: 'bold' }}>{item.sp_title}</Text>
                                <Text style={{ fontSize: 8, color: '#666' }}>{item.sp_code} • {item.sp_type}</Text>
                                <Text style={{ fontSize: 8, color: '#666' }}>Valid: {formatDate(item.start_date)} - {formatDate(item.end_date)}</Text>
                            </View>
                            <View style={styles.colQty}>
                                <Text>{item.order_qty}</Text>
                            </View>
                            <View style={styles.colPrice}>
                                <Text>{formatCurrency(item.sales_price)}</Text>
                            </View>
                            <View style={styles.colTotal}>
                                <Text>{formatCurrency(item.sub_total)}</Text>
                            </View>
                        </View>
                    ))}
                </View>

                {/* Totals */}
                <View style={styles.totalsSection}>
                    <View style={styles.totalRow}>
                        <Text style={styles.totalLabel}>Sub Total:</Text>
                        <Text style={styles.totalValue}>{formatCurrency(invoice.total_items_sum)}</Text>
                    </View>
                    {parseFloat(invoice.total_discount) > 0 && (
                        <View style={styles.totalRow}>
                            <Text style={styles.totalLabel}>Discount:</Text>
                            <Text style={styles.totalValue}>- {formatCurrency(invoice.total_discount)}</Text>
                        </View>
                    )}
                     {parseFloat(invoice.total_tax) > 0 && (
                        <View style={styles.totalRow}>
                            <Text style={styles.totalLabel}>Tax:</Text>
                            <Text style={styles.totalValue}>+ {formatCurrency(invoice.total_tax)}</Text>
                        </View>
                    )}
                    <View style={[styles.totalRow, styles.grandTotal]}>
                        <Text style={styles.totalLabel}>Grand Total:</Text>
                         <Text style={{ fontSize: 14, fontWeight: 'bold' }}>{invoice.invoice_currency} {formatCurrency(invoice.invoice_total)}</Text>
                    </View>
                    
                    <View style={{ height: 10 }} />

                    <View style={styles.totalRow}>
                        <Text style={styles.totalLabel}>Paid Amount:</Text>
                        <Text style={styles.totalValue}>{formatCurrency(invoice.total_paid_amount)}</Text>
                    </View>
                    <View style={styles.totalRow}>
                        <Text style={styles.totalLabel}>Balance Due:</Text>
                        <Text style={{ ...styles.totalValue, color: 'red' }}>{formatCurrency(invoice.tobe_paid_amount - invoice.total_paid_amount)}</Text>
                    </View>
                </View>

                {/* Payments Section if any */}
                {payments && payments.length > 0 && (
                    <View style={{ marginTop: 20 }}>
                        <Text style={{ fontSize: 11, fontWeight: 'bold', marginBottom: 5 }}>Payment History</Text>
                        {payments.map((pay, idx) => (
                            <View key={idx} style={{ flexDirection: 'row', fontSize: 9, marginBottom: 2 }}>
                                <Text style={{ width: 100 }}>{new Date(pay.process_dt).toLocaleDateString()}</Text>
                                <Text style={{ width: 100 }}>{pay.payment_type}</Text>
                                <Text style={{ width: 100 }}>{pay.payment_ref}</Text>
                                <Text style={{ width: 80, textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(pay.paid_amount)}</Text>
                            </View>
                        ))}
                    </View>
                )}

                {/* Footer */}
                <View style={styles.footer}>
                    <Text>Note: {invoice.invoice_note || 'Thank you for your business!'}</Text>
                    <Text style={{ marginTop: 5 }}>System Generated Invoice</Text>
                </View>
            </Page>
        </Document>
    );
};

export default InvoicePDF;
